import { Link as RouterLink } from 'react-router-dom';
import { buildPreviewValues } from '@/features/projects/components/metricThresholds';
import { ThresholdPreviewChips } from '@/features/projects/components/metricThresholds/ThresholdPreviewChips';
import {
  buildProjectMetricThresholdMetricSettingsHref,
  type ProjectMetricThresholdMetricKey,
} from '@/features/projects/utils/projectSettingsRoute';
import {
  DAILY_STATUS_BLOCK_SIZE,
  HEADER_SUBTEXT_SX,
} from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.constants';
import { isMetricThresholdDisabled, type MetricThreshold } from '@/shared/utils/metricsThresholds';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s, typographyTokens } from '@/theme/tokens';
import {
  chartStatusDarkenCoefficient,
  lightModeChartStatusColors,
  uiTokens,
} from '@/theme/uiTokens';
import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatFrequency } from '@/ui/formatters/formatFrequency';
import { formatPercent } from '@/ui/formatters/formatPercent';
import { Box, darken, Divider, Link, Text, type Theme } from '@/ui/primitives';
import {
  ChecklistRtlOutlinedIcon,
  FlakyIcon,
  ReplayOutlinedIcon,
  TimerOutlinedIcon,
} from '@/ui/primitives/icons';
import {
  buildCalculationExampleDateLabels,
  resolveCalculationExampleEndDayOffset,
  resolveCalculationExampleDateStep,
} from './metricTrendCardExampleDates';
import {
  metricInfoCopy,
  type CalculationBlock,
  type CalculationSegment,
  type FormulaExpression,
  type MetricInfoKey,
} from './MetricTrendCardInfoContent.copy';
import type { MetricsScope } from '@/features/metrics/metricsHelpers';
import type { ThresholdDirection } from '@/features/projects/components/metricThresholds/utils';
import type { ReactElement } from 'react';

type MetricHeaderIcon = 'duration' | 'flakeScore' | 'frequency' | 'passRate';

type MetricTrendCardInfoContentProps = {
  metricKey: MetricInfoKey;
  settingsHref?: string;
  threshold?: MetricThreshold | null;
  thresholdScope?: MetricsScope;
};

const popoverTitleSx = { fontSize: '1.125rem', fontWeight: 500, lineHeight: 1.3 } as const;
const popoverHeaderSx = {
  alignItems: 'center',
  color: 'text.primary',
  display: 'flex',
  gap: s('sm'),
  minWidth: 0,
} as const;
const popoverHeaderIconSx = {
  color: 'text.primary',
  flexShrink: 0,
  height: '19px !important',
  transform: 'translateY(-1px)',
  width: '19px !important',
} as const;
const popoverBodySx = { fontSize: typographyTokens.body.fontSize, lineHeight: 1.45 } as const;
const popoverDividerSx = {
  borderColor: (theme: Theme) =>
    isLightTheme(theme) ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.24)',
  borderBottomWidth: 0.5,
} as const;
const sectionHeadingSx = { ...popoverBodySx, fontWeight: 500 } as const;
const calculationFrameSx = {
  display: 'grid',
  gap: s('xs'),
} as const;
const calculationRowSx = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  alignItems: 'center',
} as const;
const calculationStripSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
  gap: s('md'),
  minWidth: 0,
} as const;
const calculationDateHeaderSx = {
  display: 'grid',
  gap: s('md'),
  gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
  minWidth: 0,
} as const;
const calculationDateLabelSx = {
  textAlign: 'left',
  whiteSpace: 'nowrap',
} as const;
const formulaSx = {
  fontSize: typographyTokens.caption.fontSize,
  fontWeight: 300,
  lineHeight: 1.5,
} as const;
const formulaDisplaySx = {
  ...formulaSx,
  alignItems: 'center',
  color: 'text.secondary',
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: s('xs'),
  justifyContent: 'center',
  minWidth: 0,
  width: '100%',
} as const;
const formulaPanelSx = {
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  display: 'flex',
  justifyContent: 'center',
  px: s('sm'),
  py: s('lg'),
} as const;
const formulaFractionSx = {
  alignItems: 'center',
  display: 'inline-flex',
  flexDirection: 'column',
  gap: s('sm'),
  lineHeight: 1.1,
  verticalAlign: 'middle',
} as const;
const formulaFractionDividerSx = {
  alignSelf: 'stretch',
  borderTop: '1px solid currentColor',
  opacity: 0.85,
} as const;
const formulaParenthesisSx = {
  fontSize: '2.5em',
  fontWeight: 100,
  lineHeight: 1,
} as const;
const formulaSpacedOperatorSx = {
  ml: s('xs'),
} as const;
const formulaBaselineOffsetSx = {
  transform: 'translateY(-0.22em)',
} as const;
const formulaTextDownOffsetSx = {
  transform: 'translateY(-0.1em)',
} as const;
const formulaResultSx = {
  alignItems: 'center',
  display: 'inline-flex',
  flexDirection: 'column',
  gap: s('xxs'),
  lineHeight: 1.2,
} as const;
const formulaResultOffsetSx = {
  transform: 'translateY(0.08em)',
} as const;
const formulaResultUpOffsetSx = {
  transform: 'translateY(-0.08em)',
} as const;
const formulaResultUnderlineSx = {
  borderRadius: r('pill'),
  height: '2px',
  width: '100%',
} as const;
const calculationBlockLabelSx = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(13),
  fontWeight: 500,
  lineHeight: 1,
  overflow: 'hidden',
  textAlign: 'center',
  textOverflow: 'clip',
  whiteSpace: 'nowrap',
} as const;

const resolveCalculationSegmentColor = (theme: Theme, segment: CalculationSegment): string => {
  if (segment === 'empty') return 'transparent';
  const tone = segment === 'failed' ? 'red' : segment === 'passed' ? 'green' : 'orange';
  if (isLightTheme(theme)) return lightModeChartStatusColors[tone];
  return darken(uiTokens.statusColors[tone], chartStatusDarkenCoefficient);
};

const metricSettingsAnchorKeyByMetricInfoKey: Partial<
  Record<MetricInfoKey, ProjectMetricThresholdMetricKey>
> = {
  passRate: 'passRate',
  frequency: 'frequency',
  avgDuration: 'avgDuration',
  flakeScore: 'flakeScore',
};

const metricHeaderIconByMetricInfoKey: Partial<Record<MetricInfoKey, MetricHeaderIcon>> = {
  passRate: 'passRate',
  frequency: 'frequency',
  avgDuration: 'duration',
  flakeScore: 'flakeScore',
};

const thresholdHeadingByScope: Record<MetricsScope, string> = {
  executionRun: 'Current Execution Run Thresholds',
  job: 'Current Job Thresholds',
  pipeline: 'Current Pipeline Thresholds',
  stage: 'Current Stage Thresholds',
  suite: 'Current Suite Thresholds',
  test: 'Current Test Thresholds',
};

const resolveCalculationBlockLabelColor = (segment: CalculationSegment): string =>
  segment === 'failed' ? '#ffffff' : '#000000';

const resolveCalculationSegments = (block: CalculationBlock): CalculationSegment[] =>
  Array.isArray(block) ? block : [block];

const renderMetricHeaderIcon = (icon: MetricHeaderIcon | undefined): ReactElement | null => {
  if (icon === 'passRate') {
    return <ChecklistRtlOutlinedIcon aria-hidden="true" sx={popoverHeaderIconSx} />;
  }
  if (icon === 'frequency') {
    return <ReplayOutlinedIcon aria-hidden="true" sx={popoverHeaderIconSx} />;
  }
  if (icon === 'duration') {
    return <TimerOutlinedIcon aria-hidden="true" sx={popoverHeaderIconSx} />;
  }
  if (icon === 'flakeScore') {
    return <FlakyIcon aria-hidden="true" sx={popoverHeaderIconSx} />;
  }
  return null;
};

const formatThresholdValue = (metricKey: MetricInfoKey, value: number): string => {
  if (metricKey === 'passRate' || metricKey === 'flakeScore') {
    return `${formatPercent(value)}%`;
  }
  if (metricKey === 'frequency') return formatFrequency(value);
  if (metricKey === 'avgDuration') return formatDuration(value * 1000);
  return String(value);
};

const getThresholdPreviewValues = (
  metricKey: MetricInfoKey,
  threshold: MetricThreshold,
): { red: number; yellowMin: number; yellowMax: number; green: number } => {
  if (metricKey === 'passRate' || metricKey === 'flakeScore') {
    return buildPreviewValues(threshold, { min: 0, max: 100, step: 1 });
  }
  const step = metricKey === 'frequency' ? 0.1 : 1;
  return buildPreviewValues(threshold, {
    min: 0,
    max: Math.max(threshold.warn + step, threshold.target + step),
    step,
  });
};

const resolveThresholdUnderlineColor = (
  value: number | undefined,
  threshold: MetricThreshold | null | undefined,
): string | null => {
  if (value === undefined || !threshold || isMetricThresholdDisabled(threshold)) return null;
  const chipKey = getThresholdChipKey(value, threshold);
  if (chipKey === 'green') return uiTokens.statusColors.green;
  if (chipKey === 'yellow') return uiTokens.statusColors.orange;
  if (chipKey === 'red') return uiTokens.statusColors.red;
  return null;
};

const FormulaDisplay = ({
  expression,
  resultUnderlineColor,
}: {
  expression: FormulaExpression;
  resultUnderlineColor?: string | null;
}): ReactElement => (
  <Box sx={formulaPanelSx}>
    <Box component="span" role="math" aria-label={expression.ariaLabel} sx={formulaDisplaySx}>
      {expression.tokens.map((token, tokenIndex) => {
        const followsFraction =
          tokenIndex > 0 && expression.tokens[tokenIndex - 1].kind === 'fraction';
        if (token.kind === 'text') {
          const shouldOffsetAfterFraction = followsFraction && token.value !== ')';
          return (
            <Box
              key={`${token.value}-${String(tokenIndex)}`}
              component="span"
              sx={[
                token.value === '(' || token.value === ')' ? formulaParenthesisSx : null,
                token.value.startsWith('x ') || token.value.startsWith('=')
                  ? formulaSpacedOperatorSx
                  : null,
                shouldOffsetAfterFraction ? formulaBaselineOffsetSx : null,
                token.offset === 'down' ? formulaTextDownOffsetSx : null,
              ]}
            >
              {token.value}
            </Box>
          );
        }
        if (token.kind === 'result') {
          return (
            <Box
              key={`${token.value}-${String(tokenIndex)}`}
              component="span"
              sx={[
                formulaResultSx,
                followsFraction ? formulaBaselineOffsetSx : null,
                token.offset === 'up' ? formulaResultUpOffsetSx : formulaResultOffsetSx,
              ]}
            >
              <Box component="span">{token.value}</Box>
              {resultUnderlineColor ? (
                <Box
                  component="span"
                  sx={[formulaResultUnderlineSx, { backgroundColor: resultUnderlineColor }]}
                />
              ) : null}
            </Box>
          );
        }

        return (
          <Box
            key={`${token.numerator}-${token.denominator}-${String(tokenIndex)}`}
            component="span"
            sx={formulaFractionSx}
          >
            <Box component="span">{token.numerator}</Box>
            <Box component="span" sx={formulaFractionDividerSx} />
            <Box component="span">{token.denominator}</Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const CalculationExample = ({
  blockColumnSpan = 1,
  dateLabels,
  result,
  resultUnderlineColor,
  segmentLabels,
  segments,
}: {
  blockColumnSpan?: number;
  dateLabels: string[];
  result: FormulaExpression;
  resultUnderlineColor?: string | null;
  segmentLabels?: string[];
  segments: CalculationBlock[];
}): ReactElement => (
  <Box sx={{ display: 'grid', gap: s('lg') }}>
    <Box sx={calculationFrameSx}>
      <Box sx={calculationRowSx}>
        <Box sx={{ display: 'grid', gap: s('sm') }}>
          <Box aria-hidden="true" sx={calculationDateHeaderSx}>
            {dateLabels.map((label, labelIndex) => (
              <Text
                key={label}
                role="body"
                sx={[
                  HEADER_SUBTEXT_SX,
                  calculationDateLabelSx,
                  {
                    gridColumn: `${String(labelIndex * 2 + 1)} / span 2`,
                  },
                ]}
              >
                {label}
              </Text>
            ))}
          </Box>
          <Box sx={calculationStripSx}>
            {segments.map((block, segmentIndex) => (
              <Box
                key={`${JSON.stringify(block)}-${String(segmentIndex)}`}
                sx={{
                  backgroundColor: 'transparent',
                  border: '1px solid',
                  borderColor: resolveCalculationSegments(block).every(
                    (segment) => segment === 'empty',
                  )
                    ? 'divider'
                    : 'transparent',
                  borderRadius: r('sm'),
                  display: 'flex',
                  height: DAILY_STATUS_BLOCK_SIZE + 4,
                  gridColumn: `span ${String(blockColumnSpan)}`,
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                {resolveCalculationSegments(block).map((segment, nestedSegmentIndex) => (
                  <Box
                    key={`${segment}-${String(nestedSegmentIndex)}`}
                    sx={{
                      alignItems: 'center',
                      backgroundColor: (theme) => resolveCalculationSegmentColor(theme, segment),
                      borderLeft: nestedSegmentIndex === 0 ? 'none' : '1px solid #202127',
                      display: 'flex',
                      flex: 1,
                      justifyContent: 'center',
                      minWidth: 0,
                    }}
                  >
                    {segmentLabels?.[segmentIndex] ? (
                      <Box
                        component="span"
                        sx={[
                          calculationBlockLabelSx,
                          { color: resolveCalculationBlockLabelColor(segment) },
                        ]}
                      >
                        {segmentLabels[segmentIndex]}
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
    <FormulaDisplay expression={result} resultUnderlineColor={resultUnderlineColor} />
  </Box>
);

export const MetricTrendCardInfoContent = ({
  metricKey,
  settingsHref,
  threshold,
  thresholdScope = 'executionRun',
}: MetricTrendCardInfoContentProps): ReactElement => {
  const infoCopy = metricInfoCopy[metricKey];
  const thresholdDisabled = threshold ? isMetricThresholdDisabled(threshold) : false;
  const previewValues =
    threshold && !thresholdDisabled ? getThresholdPreviewValues(metricKey, threshold) : null;
  const thresholdDirection = threshold?.direction as ThresholdDirection | undefined;
  const headerIcon = renderMetricHeaderIcon(metricHeaderIconByMetricInfoKey[metricKey]);
  const calculationDateLabels = buildCalculationExampleDateLabels({
    dayStep: resolveCalculationExampleDateStep({
      blockColumnSpan: infoCopy.calculationBlockColumnSpan,
      segmentCount: infoCopy.calculationSegments.length,
    }),
    endDayOffset: resolveCalculationExampleEndDayOffset({
      blockColumnSpan: infoCopy.calculationBlockColumnSpan,
      segmentCount: infoCopy.calculationSegments.length,
    }),
  });
  const resultUnderlineColor = resolveThresholdUnderlineColor(
    infoCopy.calculationResultValue,
    threshold,
  );
  const settingsLinkHref = settingsHref
    ? buildProjectMetricThresholdMetricSettingsHref(
        settingsHref,
        thresholdScope,
        metricSettingsAnchorKeyByMetricInfoKey[metricKey],
      )
    : undefined;

  return (
    <Box sx={{ display: 'grid', gap: s('xl') }}>
      <Box sx={{ display: 'grid', gap: s('sm') }}>
        <Box sx={popoverHeaderSx}>
          {headerIcon}
          <Text role="bodyStrong" sx={popoverTitleSx}>
            {infoCopy.title}
          </Text>
        </Box>
        <Box sx={{ display: 'grid', gap: s('lg') }}>
          <Box sx={{ display: 'grid', gap: s('sm') }}>
            <Text role="body" color="textSecondary" sx={popoverBodySx}>
              {infoCopy.definition}
            </Text>
            {infoCopy.secondaryDefinition ? (
              <Text role="body" color="textSecondary" sx={popoverBodySx}>
                {infoCopy.secondaryDefinition}
              </Text>
            ) : null}
          </Box>
          <FormulaDisplay expression={infoCopy.formula} />
        </Box>
      </Box>
      <Divider sx={popoverDividerSx} />
      <Box sx={{ display: 'grid', gap: s('md') }}>
        <Text role="bodyStrong" color="textPrimary" sx={sectionHeadingSx}>
          Example
        </Text>
        <CalculationExample
          blockColumnSpan={infoCopy.calculationBlockColumnSpan}
          dateLabels={calculationDateLabels}
          result={infoCopy.calculationResult}
          resultUnderlineColor={resultUnderlineColor}
          segmentLabels={infoCopy.calculationSegmentLabels}
          segments={infoCopy.calculationSegments}
        />
      </Box>
      {threshold ? (
        <>
          <Divider sx={popoverDividerSx} />
          <Box sx={{ display: 'grid', gap: s('sm') }}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: s('md'),
                justifyContent: 'space-between',
              }}
            >
              <Text role="bodyStrong" color="textPrimary" sx={sectionHeadingSx}>
                {thresholdHeadingByScope[thresholdScope]}
              </Text>
              {settingsLinkHref ? (
                <Link
                  component={RouterLink}
                  rel="noopener noreferrer"
                  target="_blank"
                  to={settingsLinkHref}
                  underline="hover"
                  sx={{
                    fontSize: typographyTokens.body.fontSize,
                    fontWeight: typographyTokens.bodyStrong.fontWeight,
                    lineHeight: 1.4,
                    flexShrink: 0,
                  }}
                >
                  View
                </Link>
              ) : null}
            </Box>
            {thresholdDisabled ? (
              <Text role="body" sx={popoverBodySx}>
                Thresholds are disabled for this metric.
              </Text>
            ) : (
              previewValues &&
              thresholdDirection && (
                <ThresholdPreviewChips
                  values={previewValues}
                  formatter={(value) => formatThresholdValue(metricKey, value)}
                  direction={thresholdDirection}
                  warn={threshold.warn}
                  target={threshold.target}
                  align="spread"
                />
              )
            )}
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export type { MetricInfoKey };
